import Vue from 'vue'
import VueRouter from 'vue-router'
import store from "@/store/index";
import Cookie from "js-cookie";
import { canNavigate } from '@/libs/acl/routeProtection'
import api from '@/libs/axios'
import { getSafeAuthRedirectPath } from '@core/utils/authRedirect'
import { trackMetaPageView } from '@/libs/meta-pixel'

Vue.use(VueRouter)

const ORIGIN_QUERY_KEYS = ['ref', 'origin', 'origin_ref', 'param_origem_url'];
const SELLER_QUERY_KEYS = ['vd_id', 'vd', 'vendedor_id', 'seller_id', 'seller'];
const ORIGIN_STORAGE_KEY = 'checkout_origin_ref';
const SELLER_STORAGE_KEY = 'checkout_seller_id';
const STORE_ID_STORAGE_KEY = 'store_access_store_id';
const ACCESS_LOG_STORAGE_KEY = 'store_access_last_log';
const STORE_NICK_FALLBACK = process.env.VUE_APP_STORE_NICK || 'psiqueasy';
const ACCESS_LOG_DELAY_MS = 4000;
let accessLogTimer = null;
const AFFILIATE_STATUS_ACTIVE = 'active';
const AFFILIATE_ROUTE_NAMES = new Set([
  'affiliate-entry',
  'affiliate-form',
  'affiliate-dashboard',
  'affiliate-products',
  'affiliate-marketing-materials',
  'affiliate-access',
  'affiliate-sales',
  'affiliate-reports',
  'affiliate-settings',
  'affiliate-blocked',
]);

const normalizeAffiliateStatus = (status) =>
  String(status || '')
    .trim()
    .toLowerCase();

const isAffiliateBlockedStatus = (status) => {
  const normalized = normalizeAffiliateStatus(status);
  return normalized.length > 0 && normalized !== AFFILIATE_STATUS_ACTIVE;
};

const readOriginFromRoute = (route) => {
  if (!route || !route.query) return null;
  const found = ORIGIN_QUERY_KEYS
    .map(key => route.query[key])
    .find(val => typeof val === 'string' && val.trim().length);
  return found ? found.toString().trim() : null;
};

const readSellerFromRoute = (route) => {
  if (!route || !route.query) return null;
  const entry = SELLER_QUERY_KEYS
    .map(key => ({ key, val: route.query[key] }))
    .find(({ val }) => typeof val === 'string' && val.trim().length);
  if (!entry) return null;
  return {
    key: entry.key,
    value: entry.val.toString().trim(),
  };
};

const persistOriginRef = (value) => {
  try {
    if (typeof sessionStorage === 'undefined') return;
    if (!value) {
      sessionStorage.removeItem(ORIGIN_STORAGE_KEY);
      return;
    }
    sessionStorage.setItem(ORIGIN_STORAGE_KEY, value);
  } catch (err) {
    // ignore storage errors
  }
};

const persistSellerId = (value) => {
  try {
    if (typeof sessionStorage === 'undefined') return;
    if (!value) {
      sessionStorage.removeItem(SELLER_STORAGE_KEY);
      return;
    }
    sessionStorage.setItem(SELLER_STORAGE_KEY, value);
  } catch (err) {
    // ignore storage errors
  }
};

const getStoredOriginRef = () => {
  try {
    if (typeof sessionStorage === 'undefined') return null;
    const val = sessionStorage.getItem(ORIGIN_STORAGE_KEY);
    return val && val.toString().trim().length ? val.toString().trim() : null;
  } catch (err) {
    return null;
  }
};

const getStoredSellerId = () => {
  try {
    if (typeof sessionStorage === 'undefined') return null;
    const val = sessionStorage.getItem(SELLER_STORAGE_KEY);
    return val && val.toString().trim().length ? val.toString().trim() : null;
  } catch (err) {
    return null;
  }
};

const getStoredStoreId = () => {
  try {
    if (typeof sessionStorage === 'undefined') return null;
    const val = sessionStorage.getItem(STORE_ID_STORAGE_KEY);
    if (!val) return null;
    const parsed = Number(val);
    return Number.isFinite(parsed) ? parsed : null;
  } catch (err) {
    return null;
  }
};

const persistStoreId = (value) => {
  try {
    if (typeof sessionStorage === 'undefined') return;
    if (!value && value !== 0) {
      sessionStorage.removeItem(STORE_ID_STORAGE_KEY);
      return;
    }
    sessionStorage.setItem(STORE_ID_STORAGE_KEY, String(value));
  } catch (err) {
    // ignore storage errors
  }
};

const resolveStoreId = async () => {
  const envStoreId = Number(process.env.VUE_APP_STORE_ID);
  if (Number.isFinite(envStoreId) && envStoreId > 0) return envStoreId;

  const cached = getStoredStoreId();
  if (cached) return cached;

  try {
    const response = await api.get(`/customer/stores/${STORE_NICK_FALLBACK}/info`);
    const store = response?.data || {};
    const rawId = store.id ?? store.store_id ?? store.storeId ?? store?.store?.id ?? null;
    const parsed = Number(rawId);
    if (Number.isFinite(parsed) && parsed > 0) {
      persistStoreId(parsed);
      return parsed;
    }
  } catch (err) {
    // ignore lookup errors
  }

  return null;
};

const resolveAccessContext = (route) => {
  if (!route) return null;

  if (route.name === 'e-commerce-shop') {
    return { productType: null, productId: null };
  }

  if (route.name === 'e-commerce-product-details') {
    const type = String(route.params?.type || '').toLowerCase();
    const id = Number(route.params?.id);
    if (!Number.isFinite(id) || id <= 0) return null;

    let productType = null;
    if (type === 'e') productType = 'ebook';
    if (type === 'c') productType = 'collection';
    if (type === 'cb') productType = 'combo';
    if (!productType) return null;

    return { productType, productId: id };
  }

  return null;
};

const shouldSkipAccessLog = (key) => {
  try {
    if (typeof sessionStorage === 'undefined') return false;
    const raw = sessionStorage.getItem(ACCESS_LOG_STORAGE_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.key !== key) return false;
    const now = Date.now();
    const elapsed = now - Number(parsed.ts || 0);
    return elapsed < 30000; // 30s
  } catch (err) {
    return false;
  }
};

const setAccessLogKey = (key) => {
  try {
    if (typeof sessionStorage === 'undefined') return;
    sessionStorage.setItem(ACCESS_LOG_STORAGE_KEY, JSON.stringify({ key, ts: Date.now() }));
  } catch (err) {
    // ignore storage errors
  }
};

const logStoreAccess = async (route) => {
  const context = resolveAccessContext(route);
  if (!context) return;

  const storeId = await resolveStoreId();
  if (!storeId) return;

  const originRef = readOriginFromRoute(route) || getStoredOriginRef();
  const sellerRef = readSellerFromRoute(route)?.value || getStoredSellerId();
  const source = route?.query?.source || route?.query?.utm_source || null;

  const payload = {
    store_id: storeId,
    product_type: context.productType,
    product_id: context.productId,
    ref: originRef || null,
    vendedor_user_id: sellerRef ? Number(sellerRef) || sellerRef : null,
    source: source || null,
  };

  const accessKey = [
    storeId,
    context.productType || 'store',
    context.productId || 0,
    payload.ref || '',
    payload.vendedor_user_id || '',
  ].join('|');

  if (shouldSkipAccessLog(accessKey)) return;

  try {
    await api.post('/store/access', payload);
    setAccessLogKey(accessKey);
  } catch (err) {
    // Mantém silencioso para não impactar navegação pública
    console.warn('[store/access]', err?.response?.status || err?.message || err);
  }
};

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  scrollBehavior: function (to, from, savedPosition) {
    if (to.hash) {
      return { selector: to.hash }
    } else {
      return { x: 0, y: 0 }
    }
  },
  routes: [

    // *===============================================---*
    // *--------- PUBLIC ---------------------------------*
    // *===============================================---*
    {
      path: '/login/:locale?',
      name: 'login',
      component: () => import(/* webpackChunkName: "login" */'@/views/authentication/Login.vue'),
      meta: {
        layout: 'full',
        action: 'read',
        resource: 'Public'
      },
    },
    {
      path: '/register/:locale?',
      name: 'register',
      component: () => import(/* webpackChunkName: "register" */'@/views/authentication/Register.vue'),
      meta: {
        layout: 'full',
        action: 'read',
        resource: 'Public'
      },
    },
    {
      path: '/forgot-password',
      name: 'forgot-password',
      component: () => import(/* webpackChunkName: "forgot_password" */'@/views/authentication/ForgotPassword.vue'),
      meta: {
        layout: 'full',
        resource: 'Public',
        redirectIfLoggedIn: true,
      },
    },
    {
      path: '/reset-password/:token/:email',
      name: 'reset-password',
      component: () => import(/* webpackChunkName: "reset_password" */'@/views/authentication/ResetPassword.vue'),
      meta: {
        layout: 'full',
        action: 'read',
        resource: 'Public'
      },
    },
    {
      path: '/new-password/:token/:email',
      name: 'new-password',
      component: () => import(/* webpackChunkName: "cew_password" */'@/views/authentication/NewPassword.vue'),
      meta: {
        layout: 'full',
        action: 'read',
        resource: 'Public'
      },
    },
    {
      path: '/oauth/callback/token/:token',
      name: 'callback-oauth',
      component: () => import(/* webpackChunkName: "callback_oauth" */'@/views/authentication/CallbackOAuth.vue'),
      meta: {
        layout: 'full',
        action: 'read',
        resource: 'Public'
      },
    },
    {
      path: '/verify-email/:id/:token',
      name: 'verify-email',
      component: () => import(/* webpackChunkName: "verify_email" */'@/views/authentication/VerifyEmail.vue'),
      meta: {
        layout: 'full',
        action: 'read',
        resource: 'Public'
      },
    },
    {
      path: '/verify-email/:id/:email/change/mail',
      name: 'verify-email-change',
      component: () => import(/* webpackChunkName: "verify_email_change" */'@/views/authentication/VerifyEmailChange.vue'),
      meta: {
        layout: 'full',
        action: 'read',
        resource: 'Public'
      },
    },
    {
      path: '/error-404',
      name: 'error-404',
      component: () => import(/* webpackChunkName: "error404" */'@/views/error/Error404.vue'),
      meta: {
        layout: 'full',
        action: 'read',
        resource: 'Public'
      },
    },
    {
      path: '/not-authorized',
      name: 'not-authorized',
      // ! Update import path
      component: () => import(/* webpackChunkName: "not_authorized" */'@/views/error/NotAuthorized.vue'),
      meta: {
        layout: 'full',
        action: 'read',
        resource: 'Public'
      },
    },
    {
      path: '/terms-of-use',
      name: 'terms-of-use',
      // ! Update import path
      component: () => import(/* webpackChunkName: "terms_of_use" */'@/views/TermsOfUse.vue'),
      meta: {
        layout: 'full',
        action: 'read',
        resource: 'Public',
      },
    },
    {
      path: '/first/access/:params/:params2',
      name: 'first-access',
      component: () => import(/* webpackChunkName: "first_access" */'@/views/customer/first-access/FirstAccess.vue'),
      meta: {
        layout: 'full',
        action: 'read',
        resource: 'Public',
      },
    },
    // *===============================================---*
    // *--------- CUSTOMER -------------------------------*
    // *===============================================---*
    {
      path: '/',
      name: 'redirect',
      component: () => import(/* webpackChunkName: "redirect" */'@/views/Redirect.vue'),
      meta: {
        action: 'read',
        resource: 'Public',
      },
    },
    // rota visualizador de epub
    {
      path: '/epub/:purchase/:id/:key/:back',
      name: 'epub',
      component: () => import(/* webpackChunkName: "epub_controller" */'@/views/epub/epubController.vue'),
      meta: {
        layout: 'full',

      },
    },
    {
      path: '/home',
      name: 'client-home',
      component: () => import(/* webpackChunkName: "home_page" */'@/views/customer/home/Home.vue'),
      meta: {
        pageTitle: 'Home',
        breadcrumb: [
          {
            text: 'Home',
            active: true,
          },
        ],

      },
    },
    {
      path: '/faq',
      name: 'faq',
      component: () => import(/* webpackChunkName: "faq_page" */'@/views/customer/faq/Faq.vue'),
      meta: {
        pageTitle: 'FAQ',
        breadcrumb: [
          {
            text: 'FAQ',
            active: true,
          },
        ],

      },
    },
    {
      path: '/view-pdf/:idSale/:idEbook/:page/:back',
      name: 'view_pdf',
      component: () => import(/* webpackChunkName: "customer_ebook_pdf_view" */'@/views/customer/home/ebook-view/CustomerEbookPdfView.vue'),
      meta: {
        layout: 'full',
        action: 'read',
        resource: 'Public',

      },
    },

    {
      path: '/my-products',
      name: 'my_products',
      component: () => import(/* webpackChunkName: "my_products_page" */'@/views/customer/my-products/MyProducts.vue'),
      meta: {
        pageTitle: 'My products',
        breadcrumb: [
          {
            text: 'My products',
            active: true,
          },
        ],

      },
    },
    {
      path: '/my-collection/:idPurchase/:idCollection/:nameCollection', //#
      name: 'my_collection',
      component: () => import(/* webpackChunkName: "my_collection" */'@/views/customer/my-products/MyCollection.vue'),
      meta: {
        pageTitle: 'My collection',
        breadcrumb: [
          {
            text: 'My products',
            to: '/my-products',
          },
          {
            text: 'My collection',
            active: true,
          },
        ],

      },
    },
    {
      path: '/my-combo-collection/:idCollection/:idCombo/:idPurchase/:nameCollection',
      name: 'my_combo-collection',
      component: () => import(/* webpackChunkName: "my_combo_collection" */'@/views/customer/my-products/MyComboCollection.vue'),
      meta: {
        pageTitle: 'My collection',
        breadcrumb: [
          {
            text: 'My products',
            to: '/my-products',
          },
          {
            text: 'My collection',
            active: true,
          },
        ],

      },
    },
    {
      path: '/my-combo/:idCombo/p/:idPurchase/:nameCombo',
      name: 'my_combo',
      component: () => import(/* webpackChunkName: "my_combo" */'@/views/customer/my-products/MyCombo.vue'),
      meta: {
        pageTitle: 'Voltar',
        breadcrumb: [
          {
            text: 'My products',
            to: '/my-products',
          },
          {
            text: 'My combo',
            active: true,
          },
        ],

      },
    },
    {
      path: '/print-advanced/:idPurchase/:idEbook',
      name: 'print_advanced',
      component: () => import(/* webpackChunkName: "print_advanced" */'@/views/customer/my-products/print-advanced/PrintAdvanced.vue'),
      meta: {
        pageTitle: 'My collection',
        breadcrumb: [
          {
            text: 'My products',
            to: '/my-products',
          },
          {
            text: 'Print Advanced',
            active: true,
          },
        ],

      },
    },
    {
      path: '/my-requests',
      name: 'my_requests',
      component: () => import(/* webpackChunkName: "my_requests_list" */'@/views/customer/my-requests/MyRequestsList.vue'),
      meta: {
        pageTitle: 'My requests',
        breadcrumb: [
          {
            text: 'My requests',
            active: true,
          },
        ],

      },
    },
    {
      path: '/my-request-preview/refunds/:id',
      name: 'my_requests_refund',
      component: () => import(/* webpackChunkName: "my_requests_refund" */'@/views/customer/my-requests/MyRequestRefund.vue'),
      meta: {
        pageTitle: 'My requests',
        breadcrumb: [
          {
            text: 'Refund',
            active: true,
          },
        ],

      },
    },
    {
      path: '/my-ticket-create/tiicket',
      name: 'my_ticket_create',
      component: () => import(/* webpackChunkName: "my_ticket_create" */'@/views/customer/my-tickets/MyTicketCreate.vue'),
      meta: {
        pageTitle: 'Create ticket',
        breadcrumb: [
          {
            text: 'Ticket',
            active: true,
          },
        ],

      },
    },


    {
      path: '/my-request-preview/:id',
      name: 'my_request_preview',
      component: () => import(/* webpackChunkName: "my_request_preview" */'@/views/customer/my-requests/MyRequestPreview.vue'),
      meta: {
        
      },
    },
    {
      path: '/pending-payment/:pendingId',
      name: 'pending_payment_verification',
      component: () => import(/* webpackChunkName: "pending_payment_verification" */'@/views/e-commerce/e-commerce-checkout/PendingPaymentVerification.vue'),
      meta: {
        action: 'read',
        resource: 'Public',
      },
    },
    {
      path: '/store-offers',
      name: 'store_offers',
      component: () => import(/* webpackChunkName: "store_offers" */'@/views/customer/StoreOffers.vue'),
      meta: {
        pageTitle: 'Store offers',
        breadcrumb: [
          {
            text: 'Store offers',
            active: true,
          },
        ],
        action: 'read',
        resource: 'Public',
      },
    },
    {
      path: '/account-setting',
      name: 'account-setting',
      component: () => import(/* webpackChunkName: "account_setting" */'@/views/customer/account-setting/AccountSetting.vue'),
      meta: {
        pageTitle: 'Account Settings',
        breadcrumb: [
          {
            text: 'Account Settings',
            active: true,
          },
        ],

      },
    },

    {
      path: '/budget/:params',
      name: 'budget',
      component: () => import(/* webpackChunkName: "budget_page" */'@/views/customer/sales-preview/Budget.vue'),
      meta: {
        layout: 'full',
        action: 'read',
        resource: 'Public',
      },
    },

    // *===============================================---*
    // *--------- ECOMMERCE  ---------------------------------------*
    // *===============================================---*
    {
      //sem parametro de paginação
      path: '/e-commerce/shop',
      name: 'e-commerce-shop',
      component: () => import(/* webpackChunkName: "e_commerce_shop_page" */'@/views/e-commerce/e-commerce-shop/ECommerceShop.vue'),
      props: route => ({ page: parseInt(route.query.page) || 1 }),
      meta: {
        contentRenderer: 'sidebar-left-detached',
        contentClass: 'ecommerce-application',
        pageTitle: 'Shop',
        breadcrumb: [
          {
            text: 'Ofertas da loja',
            active: true,
          },
        ],
        action: 'read',
        resource: 'Public',
      },
    }, //###########
    {
      path: '/my-tickets',
      name: 'my_tickets',
      component: () => import(/* webpackChunkName: "my_tickets" */'@/views/customer/my-tickets/MyTicketsList.vue'),
      meta: {
        pageTitle: 'My tickets',
        breadcrumb: [
          {
            text: 'My tickets',
            active: true,
          },
        ],

      },
    },
    {
      path: '/my-tickets-preview/:protocolNumber',
      name: 'my_tickets_preview',
      component: () => import(/* webpackChunkName: "my_tickets_preview" */'@/views/customer/my-tickets/MyTicketsPreview.vue'),
      meta: {
        pageTitle: 'My tickets',
        breadcrumb: [
          {
            text: 'My tickets',
            active: true,
          },
        ],
      },
    },
    {
      path: '/e-commerce/promotions',
      name: 'e-commerce-promotions',
      component: () => import(/* webpackChunkName: "e_commerce_promotions" */'@/views/e-commerce/e-commerce-promotions/ECommercePromotions.vue'),
      meta: {
        contentRenderer: 'sidebar-left-detached',
        contentClass: 'ecommerce-application',
        pageTitle: 'Promotions',
        breadcrumb: [
          {
            text: 'Promoções da loja',
            active: true,
          },
        ],
        action: 'read',
        resource: 'Public',
      },
    },
    {
      path: '/e-commerce/shop/upcoming',
      name: 'e-commerce-shop-upcoming',
      component: () => import(/* webpackChunkName: "e_commerce_shop_upcoming" */'@/views/e-commerce/e-commerce-shop-upcoming/ECommerceShopUpcoming.vue'),
      meta: {

        contentClass: 'ecommerce-application',
        pageTitle: 'Upcoming Releases',
        breadcrumb: [
          {
            text: 'Próximos lançamentos',
            active: true,
          },
        ],
        action: 'read',
        resource: 'Public',
      },
    },
    {
      path: '/afiliados',
      name: 'affiliate-entry',
      component: () => import(/* webpackChunkName: "affiliate_entry" */'@/views/customer/affiliates/pages/AffiliateEntry.vue'),
      meta: {
        pageTitle: 'Afiliados',
        breadcrumb: [
          {
            text: 'Afiliados',
            active: true,
          },
        ],
        action: 'read',
        resource: 'Auth',
      },
    },
    {
      path: '/afiliados/cadastro',
      name: 'affiliate-form',
      component: () => import(/* webpackChunkName: "affiliate_form" */'@/views/customer/affiliates/pages/AffiliateForm.vue'),
      meta: {
        pageTitle: 'Ativar conta de afiliado',
        breadcrumb: [
          {
            text: 'Afiliados',
            to: '/afiliados',
          },
          {
            text: 'Cadastro',
            active: true,
          },
        ],
        action: 'read',
        resource: 'Auth',
      },
    },
    {
      path: '/afiliados/bloqueio',
      name: 'affiliate-blocked',
      component: () => import(/* webpackChunkName: "affiliate_blocked" */'@/views/customer/affiliates/pages/AffiliateBlocked.vue'),
      meta: {
        pageTitle: 'Afiliados - Bloqueio',
        breadcrumb: [
          {
            text: 'Afiliados',
            to: '/afiliados',
          },
          {
            text: 'Bloqueio',
            active: true,
          },
        ],
        action: 'read',
        resource: 'Auth',
      },
    },
    {
      path: '/afiliados/dashboard',
      name: 'affiliate-dashboard',
      component: () => import(/* webpackChunkName: "affiliate_dashboard" */'@/views/customer/affiliates/pages/AffiliateDashboard.vue'),
      meta: {
        pageTitle: 'Painel de afiliados',
        breadcrumb: [
          {
            text: 'Afiliados',
            to: '/afiliados',
          },
          {
            text: 'Painel',
            active: true,
          },
        ],
        action: 'read',
        resource: 'Auth',
      },
    },
    {
      path: '/afiliados/produtos',
      name: 'affiliate-products',
      component: () => import(/* webpackChunkName: "affiliate_products" */'@/views/customer/affiliates/pages/AffiliateProducts.vue'),
      meta: {
        pageTitle: 'Afiliados - Produtos',
        breadcrumb: [
          { text: 'Afiliados', to: '/afiliados' },
          { text: 'Produtos', active: true },
        ],
        action: 'read',
        resource: 'Auth',
      },
    },
    {
      path: '/afiliados/materiais-divulgacao',
      name: 'affiliate-marketing-materials',
      component: () => import(/* webpackChunkName: "affiliate_marketing_materials" */'@/views/customer/affiliates/pages/AffiliateMarketingMaterials.vue'),
      meta: {
        pageTitle: 'Afiliados - Material de Divulgação',
        breadcrumb: [
          { text: 'Afiliados', to: '/afiliados' },
          { text: 'Material de Divulgação', active: true },
        ],
        action: 'read',
        resource: 'Auth',
      },
    },
    {
      path: '/afiliados/acessos',
      name: 'affiliate-access',
      component: () => import(/* webpackChunkName: "affiliate_accesses" */'@/views/customer/affiliates/pages/AffiliateAccesses.vue'),
      meta: {
        pageTitle: 'Afiliados - Acessos',
        breadcrumb: [
          { text: 'Afiliados', to: '/afiliados' },
          { text: 'Acessos', active: true },
        ],
        action: 'read',
        resource: 'Auth',
      },
    },
    {
      path: '/afiliados/vendas',
      name: 'affiliate-sales',
      component: () => import(/* webpackChunkName: "affiliate_sales" */'@/views/customer/affiliates/pages/AffiliateSales.vue'),
      meta: {
        pageTitle: 'Afiliados - Vendas',
        breadcrumb: [
          { text: 'Afiliados', to: '/afiliados' },
          { text: 'Vendas', active: true },
        ],
        action: 'read',
        resource: 'Auth',
      },
    },
    {
      path: '/afiliados/relatorios',
      name: 'affiliate-reports',
      component: () => import(/* webpackChunkName: "affiliate_reports" */'@/views/customer/affiliates/pages/AffiliateReports.vue'),
      meta: {
        pageTitle: '📊 Extrato do afiliado',
        breadcrumb: [
          { text: 'Afiliados', to: '/afiliados' },
          { text: 'Extrato', active: true },
        ],
        action: 'read',
        resource: 'Auth',
      },
    },
    {
      path: '/afiliados/configuracoes',
      name: 'affiliate-settings',
      component: () => import(/* webpackChunkName: "affiliate_settings" */'@/views/customer/affiliates/pages/AffiliateSettings.vue'),
      meta: {
        pageTitle: 'Afiliados - Configurações',
        breadcrumb: [
          { text: 'Afiliados', to: '/afiliados' },
          { text: 'Configurações', active: true },
        ],
        action: 'read',
        resource: 'Auth',
      },
    },
    {
      path: '/e-commerce/affiliates',
      name: 'e-commerce-affiliates',
      component: () => import(/* webpackChunkName: "e_commerce_affiliates" */'@/views/e-commerce/e-commerce-affiliates/ECommerceAffiliates.vue'),
      meta: {
        contentClass: 'ecommerce-application',
        pageTitle: 'Affiliate Program',
        breadcrumb: [
          {
            text: 'Affiliate Program',
            active: true,
          },
        ],
        action: 'read',
        resource: 'Public',
      },
    },
    // Checkout (colocado antes da rota dinâmica /e-commerce/:type/:id para evitar colisão)
    {
      path: '/e-commerce/checkout',
      redirect: { name: 'apps-e-commerce-checkout-cart' },
      meta: {
        pageTitle: 'Checkout',
        contentClass: 'ecommerce-application',
        breadcrumb: [
          {
            text: 'ECommerce',
          },
          {
            text: 'Checkout',
            active: true,
          },
        ],
        action: 'read',
        resource: 'Public',
      },
    },
    {
      path: '/e-commerce/checkout/carrinho',
      name: 'apps-e-commerce-checkout-cart',
      component: () => import(/* webpackChunkName: "apps_e_commerce_checkout_cart" */'@/views/e-commerce/e-commerce-checkout/pages/cart/CheckoutCartPage.vue'),
      meta: {
        pageTitle: 'Checkout',
        contentClass: 'ecommerce-application',
        breadcrumb: [
          {
            text: 'ECommerce',
          },
          {
            text: 'Checkout',
            active: true,
          },
        ],
        action: 'read',
        resource: 'Public',
      },
    },
    {
      path: '/e-commerce/checkout/identificacao',
      name: 'apps-e-commerce-checkout-identification',
      component: () => import(/* webpackChunkName: "apps_e_commerce_checkout_identification" */'@/views/e-commerce/e-commerce-checkout/pages/identification/CheckoutIdentificationPage.vue'),
      meta: {
        pageTitle: 'Checkout',
        contentClass: 'ecommerce-application',
        breadcrumb: [
          {
            text: 'ECommerce',
          },
          {
            text: 'Checkout',
            active: true,
          },
        ],
        action: 'read',
        resource: 'Public',
      },
    },
    {
      path: '/e-commerce/checkout/endereco',
      name: 'apps-e-commerce-checkout-address',
      component: () => import(/* webpackChunkName: "apps_e_commerce_checkout_address" */'@/views/e-commerce/e-commerce-checkout/pages/address/CheckoutAddressPage.vue'),
      meta: {
        pageTitle: 'Checkout',
        contentClass: 'ecommerce-application',
        breadcrumb: [
          {
            text: 'ECommerce',
          },
          {
            text: 'Checkout',
            active: true,
          },
        ],
        action: 'read',
        resource: 'Public',
      },
    },
    {
      path: '/e-commerce/checkout/pagamento',
      name: 'apps-e-commerce-checkout-payment',
      component: () => import(/* webpackChunkName: "apps_e_commerce_checkout_payment" */'@/views/e-commerce/e-commerce-checkout/pages/payment/CheckoutPaymentPage.vue'),
      meta: {
        pageTitle: 'Checkout',
        contentClass: 'ecommerce-application',
        breadcrumb: [
          {
            text: 'ECommerce',
          },
          {
            text: 'Checkout',
            active: true,
          },
        ],
        action: 'read',
        resource: 'Public',
      },
    },
    {//#
      path: '/e-commerce/:type/:id',
      name: 'e-commerce-product-details',
      component: () => import(/* webpackChunkName: "e_commerce_product_details" */'@/views/e-commerce/e-commerce-product-details/ECommerceProductDetails.vue'),
      meta: {
        pageTitle: 'Product Details',
        contentClass: 'ecommerce-application',
        breadcrumb: [
          {
            text: 'Shop',
            to: '/e-commerce/shop',
          },
          {
            text: 'Product Details',
            active: true,
          },
        ],
        action: 'read',
        resource: 'Public',
      },
    },

    {
      path: '/epub-preview/:id/:key/:back',
      name: 'epub-preview',
      component: () => import(/* webpackChunkName: "epub_preview_page" */'@/views/epub/epubControllerPreview.vue'),
      meta: {
        layout: 'full',
        action: 'read',
        resource: 'Public'
      },
    },

    {
      path: '/payss/checkout/:linkId',
      name: 'payment-linkss',
      component: () => import(/* webpackChunkName: "payment_linkss" */'@/views/customer/payment-link/PaymentLink.vue'),
      meta: {
        layout: 'full',
        action: 'read',
        resource: 'Public',
      },
    },



    // *===============================================---*
    // *--------- ERROR ---- -----------------------------*
    // *===============================================---*
    {
      path: '*',
      redirect: 'error-404',
    },
  ],
})

// Propaga os parâmetros de origem (ref/...) e vendedor (vd/...) na sessão atual
router.beforeEach((to, from, next) => {
  const originFromRoute = readOriginFromRoute(to);
  const sellerFromRoute = readSellerFromRoute(to);
  const fromHadOrigin = from && ORIGIN_QUERY_KEYS.some(key => from?.query && from.query[key]);
  const fromHadSeller = from && SELLER_QUERY_KEYS.some(key => from?.query && from.query[key]);

  // Se chegou com ref/seller na URL, substitui o que está na sessão
  if (originFromRoute) persistOriginRef(originFromRoute);
  if (sellerFromRoute) persistSellerId(sellerFromRoute.value);

  // Se a rota atual não tem ref/seller e a anterior também não, limpa para garantir estado "sem origem"
  if (!originFromRoute && !fromHadOrigin) persistOriginRef(null);
  if (!sellerFromRoute && !fromHadSeller) persistSellerId(null);

  const storedOrigin = getStoredOriginRef();
  const storedSeller = getStoredSellerId();

  // Se não veio na URL e também não há na sessão, segue sem tocar na rota
  if (!originFromRoute && !storedOrigin && !sellerFromRoute && !storedSeller) {
    return next();
  }

  const originRef = originFromRoute || storedOrigin || null;
  const sellerRef = sellerFromRoute ? sellerFromRoute.value : storedSeller || null;
  const routeHasOrigin = ORIGIN_QUERY_KEYS.some(key => to?.query && to.query[key]);
  const routeHasSeller = SELLER_QUERY_KEYS.some(key => to?.query && to.query[key]);

  // Se já está na URL, apenas segue
  if ((!originRef || routeHasOrigin) && (!sellerRef || routeHasSeller)) {
    return next();
  }

  // Injeta ref/vd na URL mantendo query atual
  const mergedQuery = { ...to.query };
  if (originRef && !routeHasOrigin) {
    mergedQuery.ref = originRef;
  }
  if (sellerRef && !routeHasSeller) {
    const sellerKey = (sellerFromRoute && sellerFromRoute.key) || 'vd_id';
    mergedQuery[sellerKey] = sellerRef;
  }
  const navTarget = {
    name: to.name,
    params: to.params,
    query: mergedQuery,
    hash: to.hash,
    replace: true,
  };
  if (!navTarget.name) {
    navTarget.path = to.path;
  }
  return next(navTarget);
});

// Router Before Each hook for route protection
router.beforeEach(async (to, _, next) => {
  const appLoadingStart = document.getElementById('loading-bg')
  if (appLoadingStart) appLoadingStart.style.display = 'block'

  let isLoggedIn = Cookie.get('oliveiraToken');
  if (!isLoggedIn && typeof localStorage !== 'undefined') {
    const storedToken = String(localStorage.getItem('oliveiraToken') || '').trim()
    if (storedToken) {
      Cookie.set('oliveiraToken', storedToken, { expires: 7 })
      isLoggedIn = storedToken
    }
  }

  const userData = await store.state.authentication.user

  if (isLoggedIn && (!userData || (Array.isArray(userData) && userData.length === 0) || (Object.keys(userData).length === 0))) {
    await store.dispatch("recoverUserData").then(async (res) => {

      store.commit("localeUserInfoCart", store.state.authentication.user);
      await store.dispatch("syncGuestCartToUser").catch(() => { });

    })
      .catch(err => {
        if (err?.response?.status === 401) {
          Cookie.remove('oliveiraToken')
          isLoggedIn = false
        } else {
          console.error('recoverUserData failed', err?.message || err)
        }
      })

  }


  if (!canNavigate(to, store.state.authentication.user)) {
    // Redirect to login if not logged in
    if (!isLoggedIn) return next({ name: 'login', query: { redirect: to.fullPath } })
  }



  // Redirect if logged in
  if (to.meta.redirectIfLoggedIn && isLoggedIn) {
    const redirect = getSafeAuthRedirectPath(to.query?.redirect)
    return next(redirect || '/')
  }

  if (isLoggedIn && AFFILIATE_ROUTE_NAMES.has(to.name)) {
    let affiliateProfile = store.state.authentication.affiliateProfile || null

    try {
      affiliateProfile = await store.dispatch('fetchAffiliateStoreProfile', {
        maxAgeMs: 60000,
      })
    } catch (err) {
      console.warn('[affiliate/profile]', err?.response?.status || err?.message || err)
    }

    const affiliateStatus = normalizeAffiliateStatus(
      affiliateProfile?.status || store.state.authentication.affiliateProfileStatus
    )
    const blockedAffiliate = isAffiliateBlockedStatus(affiliateStatus)

    if (blockedAffiliate && to.name !== 'affiliate-blocked') {
      return next({ name: 'affiliate-blocked' })
    }

    if (!blockedAffiliate && to.name === 'affiliate-blocked') {
      const isAffiliate = Boolean(store.state.authentication.isAffiliate)
      return next({ name: isAffiliate ? 'affiliate-dashboard' : 'affiliate-entry' })
    }
  }

  return next()
})

router.afterEach((to) => {
  const appLoadingEnd = document.getElementById('loading-bg')
  if (appLoadingEnd) appLoadingEnd.style.display = 'none'

  trackMetaPageView(to);

  if (accessLogTimer) {
    clearTimeout(accessLogTimer);
    accessLogTimer = null;
  }
  accessLogTimer = setTimeout(() => {
    logStoreAccess(to);
    accessLogTimer = null;
  }, ACCESS_LOG_DELAY_MS);
})

export default router
